import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ui/use-toast';
import { usersService } from '../services/users';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AgeDeclaration } from '../components/auth/age-declaration';
import ClaimMembershipDialog from '../components/invitation/ClaimMembershipDialog';
import { useAuth } from '../hooks/useAuth';
import type { User } from '../types/user';

interface InvitationInfo {
  id: number;
  email: string;
  name: string | null;
  surname: string | null;
  invitation_token: string;
}

interface GymMembershipInfo {
  gym_name: string | null;
  member_name: string;
}

interface CompetitionEntryInfo {
  event_name: string;
  entrant_name: string;
}

interface StaffInvitationInfo {
  place_name: string;
  staff_roles: string[];
}

const TOKEN_KEY = 'climb_log_auth_token';
const REFRESH_TOKEN_KEY = 'climb_log_refresh_token';
const USER_KEY = 'climb_log_user';

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation_token');
  // Where to land once the account is open. Only a path on this site: the
  // guardian accept page sends people here to open their own account first
  // and wants them back, and nothing else should be able to send them away.
  const redirect = searchParams.get('redirect');
  const landing = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/';

  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [gymMembership, setGymMembership] = useState<GymMembershipInfo | null>(null);
  const [competitionEntry, setCompetitionEntry] = useState<CompetitionEntryInfo | null>(null);
  const [staffInvitation, setStaffInvitation] = useState<StaffInvitationInfo | null>(null);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  // Accepting an invitation opens an account, the same as signing up.
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const { toast } = useToast();
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Arriving on a gym's, a competition's or a staff invitation while already
  // signed in: ask whether to attach the membership (the entry, the roles) to
  // this account or sign out and register as the invited address, rather than
  // silently offering a second sign-up.
  const showClaimDialog =
    !authLoading &&
    isAuthenticated &&
    !!invitationInfo &&
    (!!gymMembership || !!competitionEntry || !!staffInvitation);

  // Validate invitation token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!invitationToken) {
        setTokenError(t('invitation.tokenMissing'));
        setIsValidating(false);
        return;
      }

      try {
        const data = await usersService.validateInvitationToken(invitationToken);
        setInvitationInfo(data.user);
        setGymMembership(data.gym_membership ?? null);
        setCompetitionEntry(data.competition_entry ?? null);
        setStaffInvitation(data.staff_invitation ?? null);
        // Prefill what the inviter already recorded, rather than making the
        // person retype a name their gym already has on file.
        setName(data.user.name ?? "");
        setSurname(data.user.surname ?? "");
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('invitation.tokenInvalid');
        setTokenError(errorMessage);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [invitationToken, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !surname || !username || !password || !passwordConfirmation) return;
    if (!ageConfirmed) {
      setFormError(t('auth.ageConfirmationRequired'));
      return;
    }

    setFormError(null);

    if (password !== passwordConfirmation) {
      setFormError(t('auth.passwordsDontMatch'));
      return;
    }

    // Validate username format (only lowercase letters, numbers, hyphens and underscores)
    const usernameRegex = /^[a-z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setFormError(t('invitation.usernameFormatError'));
      return;
    }

    setIsLoading(true);

    try {
      const data = await usersService.acceptInvitation({
        invitation_token: invitationToken!,
        name,
        surname,
        username,
        password,
        password_confirmation: passwordConfirmation
      });

      // Extract user data from resource_owner
      const resourceOwner = data.resource_owner as { user?: User } | User;
      const userData = ('user' in resourceOwner && resourceOwner.user) ? resourceOwner.user : resourceOwner as User;

      // Save tokens and user data
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      toast({
        title: t('invitation.accepted'),
        description: data.message,
      });

      // Reload to refresh auth state — home, or wherever the link said.
      window.location.href = landing;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div className="flex justify-center items-center my-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('invitation.validating')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showClaimDialog) {
    if (gymMembership) {
      return (
        <ClaimMembershipDialog
          open
          invitationToken={invitationToken!}
          gymName={gymMembership.gym_name}
          memberName={gymMembership.member_name}
          invitedEmail={invitationInfo!.email}
        />
      );
    }
    if (competitionEntry) {
      return (
        <ClaimMembershipDialog
          open
          kind="competition"
          invitationToken={invitationToken!}
          gymName={competitionEntry.event_name}
          memberName={competitionEntry.entrant_name}
          invitedEmail={invitationInfo!.email}
        />
      );
    }
    return (
      <ClaimMembershipDialog
        open
        kind="staff"
        invitationToken={invitationToken!}
        gymName={staffInvitation!.place_name}
        memberName={invitationInfo!.email}
        invitedEmail={invitationInfo!.email}
      />
    );
  }

  // Show error if token is invalid
  if (tokenError) {
    return (
      <div className="flex justify-center items-center my-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('invitation.invalidTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
              {tokenError}
            </div>
          </CardContent>
          <CardFooter>
            <Link to="/sign-in" className="w-full">
              <Button variant="outline" className="w-full">
                {t('auth.signIn')}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center my-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('invitation.acceptInvitation')}</CardTitle>
          <CardDescription>
            {t('invitation.acceptDescription', { email: invitationInfo?.email })}
          </CardDescription>
          {staffInvitation && (
            <p className="text-sm" data-testid="staff-invitation-context">
              {t('invitation.staffInvitationDescription', {
                place: staffInvitation.place_name,
                roles: staffInvitation.staff_roles.map((role) => t(`gymSettings.${role}Role`)).join(', '),
              })}
            </p>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {formError && (
              <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">{t('profile.name')}</Label>
              <Input
                id="name"
                type="text"
                placeholder={t('profile.namePlaceholder')}
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="surname">{t('profile.surname')}</Label>
              <Input
                id="surname"
                type="text"
                placeholder={t('profile.surnamePlaceholder')}
                value={surname}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSurname(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">{t('profile.username')}</Label>
              <Input
                id="username"
                type="text"
                placeholder={t('profile.usernamePlaceholder')}
                value={username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUsername(e.target.value.toLowerCase());
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">{t('auth.confirmPassword')}</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPasswordConfirmation(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <AgeDeclaration
              checked={ageConfirmed}
              onCheckedChange={(checked) => {
                setAgeConfirmed(checked);
                if (formError) setFormError(null);
              }}
            />
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading || !ageConfirmed}>
              {isLoading ? t('invitation.accepting') : t('invitation.completeRegistration')}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/sign-in" className="text-primary hover:underline">
                {t('auth.signInNow')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
